// Customer Assist attachment upload helper. Used by both the agent
// dialog and the anon `/assist/:token` page. Storage layer is the
// `assist-attachments` Supabase Storage bucket (migration 048); paths
// are `<session_id>/<unix-ms>-<safe-filename>` so two files uploaded
// at the same instant by either side don't collide.
//
// RLS gating:
//   - Agent calls (authenticated): policy `agent_upload_assist_attachments`
//     requires an active team_member.
//   - Client calls (anon, with the per-session x-assist-token header
//     baked into the Supabase client): policy
//     `client_upload_assist_attachments` requires a matching session
//     row in `pending` or `active` state.

import type { SupabaseClient } from '@supabase/supabase-js';
import type { AttachmentMeta } from '@/lib/use-assist-channel';

/** Hard cap per file. Bigger files don't render well inline and we'd
 *  rather route them through email if someone really needs a 20MB
 *  PDF. */
export const ASSIST_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Hard cap on the number of files per message. */
export const ASSIST_ATTACHMENT_MAX_PER_MESSAGE = 3;

/** Make a filename safe for a storage path: strip path separators,
 *  drop anything weird, cap length. We DON'T try to be clever about
 *  the basename — Supabase Storage handles unicode fine. */
function sanitizeFilename(name: string): string {
  const cleaned = name
    .replace(/[\\/]+/g, '_')
    .replace(/[^A-Za-z0-9._\-+ ()]/g, '_')
    .replace(/\s+/g, '_');
  return cleaned.slice(0, 80) || 'file';
}

export interface UploadFailure {
  filename: string;
  reason: string;
}

export interface UploadResult {
  attachments: AttachmentMeta[];
  failures: UploadFailure[];
}

/** Upload a batch of files to the per-session folder. Per-file
 *  failures (oversize, network error, etc) DO NOT abort the batch —
 *  the caller renders the successful ones and surfaces the failures
 *  via a toast. */
export async function uploadAssistAttachments(args: {
  client: SupabaseClient;
  sessionId: string;
  files: File[];
}): Promise<UploadResult> {
  const { client, sessionId, files } = args;

  const out: AttachmentMeta[] = [];
  const failures: UploadFailure[] = [];

  for (const file of files) {
    if (file.size > ASSIST_ATTACHMENT_MAX_BYTES) {
      failures.push({
        filename: file.name,
        reason: `over ${(ASSIST_ATTACHMENT_MAX_BYTES / 1024 / 1024).toFixed(0)} MB limit`,
      });
      continue;
    }
    const path = `${sessionId}/${Date.now()}-${sanitizeFilename(file.name)}`;
    const { error } = await client.storage
      .from('assist-attachments')
      .upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false,
      });
    if (error) {
      failures.push({ filename: file.name, reason: error.message });
      continue;
    }
    const { data: pub } = client.storage.from('assist-attachments').getPublicUrl(path);
    out.push({
      url: pub.publicUrl,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    });
  }

  return { attachments: out, failures };
}

/** Helper for the renderer side. */
export function isInlineImage(att: AttachmentMeta): boolean {
  return att.contentType.startsWith('image/');
}

/** Human-readable size suffix for file pills. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** Extract files from a ClipboardEvent — used by both assist composes
 *  to support "paste a screenshot directly into chat" (the same
 *  Cmd-Shift-4 → Cmd-V flow agents use in Slack / Gmail). Returns
 *  a possibly-empty array; the caller decides whether to call
 *  preventDefault on the event + upload via `uploadAssistAttachments`.
 *
 *  We deliberately accept ANY file type from the clipboard (not just
 *  images) because the OS clipboard occasionally carries PDFs /
 *  text snippets too. The upload helper enforces the size + per-msg
 *  count limits; here we just lift the bytes off the clipboard.
 *
 *  Naming: screenshots arrive as anonymous "image.png" with no
 *  meaningful filename. We rename to `pasted-<timestamp>.png` so
 *  the attachment tile shows something distinguishable from any
 *  other screenshot pasted in the same session. */
export function extractClipboardFiles(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const dt = e.clipboardData;
  if (!dt) return [];
  const out: File[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (!file) continue;
    // Anonymous clipboard images come in as "image.png" with no
    // discriminating filename — rename so multiple pastes don't
    // visually collide in the pending-attachments tray.
    if (/^image\.(png|jpe?g|gif|webp)$/i.test(file.name) || !file.name) {
      const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const renamed = new File([file], `pasted-${Date.now()}.${ext}`, {
        type: file.type || 'image/png',
      });
      out.push(renamed);
    } else {
      out.push(file);
    }
  }
  return out;
}
